import { useEffect, useState } from "react";
import { Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, eur, pct, SEVERITY } from "@/lib/api";
import { Stat } from "@/components/Badges";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { t } from "@/lib/i18n";

export const Field = ({ label, id, ...props }) => (
  <div className="space-y-1.5">
    <Label htmlFor={id} className="text-xs text-muted-foreground">{label}</Label>
    <Input id={id} data-testid={id} className="bg-background num" {...props} />
  </div>
);

const INIT = { period: "Giugno 2026", revenue: 50000, covers: 2000, budget_cost: 15000, target_pct: 30, mode: "direct", actual_cost: 16500, opening_inventory: 4000, purchases: 17000, closing_inventory: 4500 };

export const FoodCostCalc = () => {
  const [f, setF] = useState(INIT);
  const [res, setRes] = useState(null);
  const [records, setRecords] = useState([]);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const num = (v) => Number(v) || 0;
  const load = () => api.get("/foodcost/records").then((r) => setRecords(r.data));
  useEffect(() => { load(); }, []);

  const payload = () => ({ revenue: num(f.revenue), covers: num(f.covers), budget_cost: num(f.budget_cost), target_pct: num(f.target_pct),
    ...(f.mode === "direct" ? { actual_cost: num(f.actual_cost) } : { opening_inventory: num(f.opening_inventory), purchases: num(f.purchases), closing_inventory: num(f.closing_inventory) }) });

  const calc = async () => {
    try { const r = await api.post("/foodcost/calc", payload()); setRes(r.data); } catch { toast.error("Controlla i valori inseriti"); }
  };
  const save = async () => {
    const p = payload();
    const actual = p.actual_cost ?? p.opening_inventory + p.purchases - p.closing_inventory;
    try {
      await api.post("/foodcost/records", { period: f.period, revenue: p.revenue, covers: p.covers, budget_cost: p.budget_cost, actual_cost: actual, target_pct: p.target_pct });
      toast.success("Periodo salvato: il Cockpit è aggiornato"); load();
    } catch { toast.error("Salvataggio non riuscito"); }
  };
  const del = (id) => api.delete(`/foodcost/records/${id}`).then(load);

  return (
    <div className="grid lg:grid-cols-[380px_1fr] gap-6">
      <div className="surface p-5 space-y-4">
        <div className="eyebrow">{t("Dati del periodo")}</div>
        <Field label={t("Periodo")} id="fc-period" value={f.period} onChange={set("period")} />
        <div className="grid grid-cols-2 gap-3">
          <Field label={t("Ricavi food (€)")} id="fc-revenue" type="number" value={f.revenue} onChange={set("revenue")} />
          <Field label={t("Coperti")} id="fc-covers" type="number" value={f.covers} onChange={set("covers")} />
          <Field label={t("Food cost preventivo (€)")} id="fc-budget" type="number" value={f.budget_cost} onChange={set("budget_cost")} />
          <Field label={t("Target %")} id="fc-target" type="number" value={f.target_pct} onChange={set("target_pct")} />
        </div>
        <div className="flex gap-1 p-1 bg-background rounded-md border border-border">
          {[["direct", t("Consumo diretto")], ["inventory", t("Da inventario")]].map(([m, l]) => (
            <button key={m} data-testid={`fc-mode-${m}`} onClick={() => setF({ ...f, mode: m })} className={`flex-1 text-xs py-1.5 rounded ${f.mode === m ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}>{l}</button>
          ))}
        </div>
        {f.mode === "direct" ? <Field label={t("Food cost consuntivo (€)")} id="fc-actual" type="number" value={f.actual_cost} onChange={set("actual_cost")} /> : (
          <div className="grid grid-cols-3 gap-3">
            <Field label={t("Inv. iniziale")} id="fc-opening" type="number" value={f.opening_inventory} onChange={set("opening_inventory")} />
            <Field label={t("Acquisti")} id="fc-purchases" type="number" value={f.purchases} onChange={set("purchases")} />
            <Field label={t("Inv. finale")} id="fc-closing" type="number" value={f.closing_inventory} onChange={set("closing_inventory")} />
          </div>
        )}
        <div className="flex gap-2"><Button data-testid="fc-calc-btn" onClick={calc} className="flex-1">{t("Calcola")}</Button><Button data-testid="fc-save-btn" variant="outline" onClick={save} className="flex-1">{t("Salva periodo")}</Button></div>
      </div>

      <div className="space-y-5">
        {res && (
          <div data-testid="fc-result" className="space-y-3 fade-up">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              <Stat testId="fc-result-actual" label={t("FC consuntivo")} value={pct(res.actual_pct)} hint={`target ${pct(res.target_pct)}`} accent={SEVERITY[res.status].color} />
              <Stat testId="fc-result-budget" label={t("FC preventivo")} value={pct(res.budget_pct)} hint={eur(res.budget_cost)} />
              <Stat testId="fc-result-variance" label={t("Scostamento")} value={`${res.variance_pts > 0 ? "+" : ""}${res.variance_pts} pt`} hint={`${eur(res.variance)} (${pct(res.variance_pct)})`} accent={Math.abs(res.variance_pts) >= 2 ? SEVERITY.attenzione.color : undefined} />
              <Stat testId="fc-result-margin-cover" label={t("Margine / coperto")} value={eur(res.margin_per_cover)} hint={t("costo {c} · ricavo {r}", { c: eur(res.cost_per_cover), r: eur(res.revenue_per_cover) })} />
            </div>
            <div className="surface p-4 text-sm" style={{ borderLeft: `3px solid ${SEVERITY[res.status].color}` }}>
              <span className="font-medium" style={{ color: SEVERITY[res.status].color }}>{SEVERITY[res.status].label}: </span>
              {res.status === "ok" ? "food cost entro il target. Consolida schede ricetta e presidia le grammature." : res.status === "attenzione" ? "food cost oltre il target di pochi punti. Verifica prezzi d'acquisto, rotazione magazzino e porzionatura." : "food cost fuori controllo. Inventario straordinario, controllo sprechi e ammanchi, revisione ricette."}
              {Math.abs(res.variance_pts) >= 2 && <span className="block mt-1 text-muted-foreground">{t("Teoria (preventivo) e realtà (consuntivo) divergono: capisci se le schede ricetta sono obsolete o se l'esecuzione non è conforme prima di toccare i prezzi.")}</span>}
            </div>
          </div>
        )}
        <div className="surface overflow-hidden">
          <div className="p-4 border-b border-border eyebrow">Periodi salvati ({records.length})</div>
          <table className="w-full text-sm">
            <thead className="text-xs text-muted-foreground"><tr className="border-b border-border"><th className="text-left p-3">{t("Periodo")}</th><th className="text-right p-3">{t("Ricavi")}</th><th className="text-right p-3">{t("Prev.")}</th><th className="text-right p-3">{t("Cons.")}</th><th className="text-right p-3">Δ pt</th><th className="text-right p-3">{t("Marg./cop.")}</th><th></th></tr></thead>
            <tbody>{records.map((r) => (
              <tr key={r.id} data-testid={`fc-record-${r.id}`} className="border-b border-border last:border-0">
                <td className="p-3 font-medium">{r.period}</td><td className="p-3 text-right num">{eur(r.revenue)}</td><td className="p-3 text-right num">{pct(r.budget_pct)}</td>
                <td className="p-3 text-right num font-semibold" style={{ color: SEVERITY[r.status].color }}>{pct(r.actual_pct)}</td>
                <td className="p-3 text-right num">{r.variance_pts > 0 ? "+" : ""}{r.variance_pts}</td><td className="p-3 text-right num">{eur(r.margin_per_cover)}</td>
                <td className="p-3 text-right"><button data-testid={`fc-record-delete-${r.id}`} onClick={() => del(r.id)} className="text-muted-foreground hover:text-destructive"><Trash2 size={14} /></button></td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
